import { createSlice } from "@reduxjs/toolkit";

import { setDeep } from "Libs/objectAccess";

import { CURRENT_DEPLOYMENT_ID, NEXT_DEPLOYMENT_ID } from "./constants";
import {
  loadDeploymentCurrent,
  loadDeploymentNext,
  loadDeploymentSettings,
  updateDeployment,
  updateDeploymentSettings
} from "./thunks";

import type { DeploymentState } from "./types";

const initialState: DeploymentState = {};

const deploymentSlice = createSlice({
  name: "deployment",
  initialState,
  reducers: {},
  extraReducers: builder => {
    // LOAD CURRENT DEPLOYMENT
    builder
      .addCase(loadDeploymentCurrent.pending, state => {
        state.loading = true;
        state.loadingCurrent = true;
      })
      .addCase(loadDeploymentCurrent.fulfilled, (state, { payload, meta }) => {
        const { organizationId, projectId, environmentId } = meta.arg;

        if (payload?.schemas) {
          state.schemas = payload.schemas;
        }
        state.loading = false;
        state.loadingCurrent = false;

        if (payload?.deployment) {
          setDeep(
            state,
            [
              "data",
              organizationId,
              projectId,
              environmentId,
              CURRENT_DEPLOYMENT_ID
            ],
            payload.deployment
          );
        }

        setDeep(
          state,
          [
            "errors",
            organizationId,
            projectId,
            environmentId,
            CURRENT_DEPLOYMENT_ID
          ],
          undefined
        );

        setDeep(
          state,
          [
            "responseCode",
            organizationId,
            projectId,
            environmentId,
            CURRENT_DEPLOYMENT_ID
          ],
          200
        );
        if (payload?.services) {
          setDeep(
            state,
            ["services", organizationId, projectId, environmentId],
            payload.services
          );
        }
      })
      .addCase(loadDeploymentCurrent.rejected, (state, action) => {
        const { organizationId, projectId, environmentId } = action.meta.arg;

        state.loading = false;
        state.loadingCurrent = false;

        setDeep(
          state,
          [
            "errors",
            organizationId,
            projectId,
            environmentId,
            CURRENT_DEPLOYMENT_ID
          ],
          action.payload
        );

        setDeep(
          state,
          [
            "responseCode",
            organizationId,
            projectId,
            environmentId,
            CURRENT_DEPLOYMENT_ID
          ],
          action.payload.code
        );
      })
      // LOAD NEXT DEPLOYMENT
      .addCase(loadDeploymentNext.pending, state => {
        state.loading = true;
        state.loadingNext = true;
      })
      .addCase(loadDeploymentNext.fulfilled, (state, { payload, meta }) => {
        const { organizationId, projectId, environmentId } = meta.arg;

        if (payload?.schemas) {
          state.schemas = payload.schemas;
        }
        state.loading = false;
        state.loadingNext = false;

        if (payload?.deployment) {
          setDeep(
            state,
            [
              "data",
              organizationId,
              projectId,
              environmentId,
              NEXT_DEPLOYMENT_ID
            ],
            payload.deployment
          );
        }

        setDeep(
          state,
          [
            "errors",
            organizationId,
            projectId,
            environmentId,
            NEXT_DEPLOYMENT_ID
          ],
          undefined
        );

        setDeep(
          state,
          [
            "responseCode",
            organizationId,
            projectId,
            environmentId,
            NEXT_DEPLOYMENT_ID
          ],
          200
        );
      })
      .addCase(loadDeploymentNext.rejected, (state, action) => {
        const { organizationId, projectId, environmentId } = action.meta.arg;

        state.loading = false;
        state.loadingNext = false;

        setDeep(
          state,
          [
            "errors",
            organizationId,
            projectId,
            environmentId,
            NEXT_DEPLOYMENT_ID
          ],
          action.payload
        );

        setDeep(
          state,
          [
            "responseCode",
            organizationId,
            projectId,
            environmentId,
            NEXT_DEPLOYMENT_ID
          ],
          action.payload.code
        );
      })
      // LOAD DEPLOYMENT SETTINGS
      .addCase(loadDeploymentSettings.pending, state => {
        state.loadingSettings = true;
      })
      .addCase(loadDeploymentSettings.fulfilled, (state, action) => {
        const { organizationId, projectId, environmentId } = action.meta.arg;

        state.loadingSettings = false;

        setDeep(
          state,
          ["settings", organizationId, projectId, environmentId],
          action.payload
        );
      })
      .addCase(loadDeploymentSettings.rejected, (state, action) => {
        const { organizationId, projectId, environmentId } = action.meta.arg;

        state.loadingSettings = false;

        setDeep(
          state,
          ["errors", organizationId, projectId, environmentId, "settings"],
          action.payload
        );
        setDeep(
          state,
          [
            "responseCode",
            organizationId,
            projectId,
            environmentId,
            "settings"
          ],
          action.payload.code
        );
      })
      // UPDATE DEPLOYMENT
      .addCase(updateDeployment.pending, (state, action) => {
        const { organizationId, projectId, environmentId } = action.meta.arg;

        state.updateLoading = true;

        setDeep(
          state,
          [
            "errors",
            organizationId,
            projectId,
            environmentId,
            CURRENT_DEPLOYMENT_ID
          ],
          undefined
        );

        setDeep(
          state,
          [
            "errors",
            organizationId,
            projectId,
            environmentId,
            NEXT_DEPLOYMENT_ID
          ],
          undefined
        );
      })
      .addCase(updateDeployment.fulfilled, (state, action) => {
        const { organizationId, projectId, environmentId } = action.meta.arg;

        state.updateLoading = false;

        setDeep(
          state,
          [
            "data",
            organizationId,
            projectId,
            environmentId,
            NEXT_DEPLOYMENT_ID
          ],
          action.payload
        );

        setDeep(
          state,
          [
            "responseCode",
            organizationId,
            projectId,
            environmentId,
            NEXT_DEPLOYMENT_ID
          ],
          200
        );
      })
      .addCase(updateDeployment.rejected, (state, action) => {
        const { organizationId, projectId, environmentId } = action.meta.arg;

        state.updateLoading = false;

        setDeep(
          state,
          [
            "errors",
            organizationId,
            projectId,
            environmentId,
            NEXT_DEPLOYMENT_ID
          ],
          action.payload
        );

        setDeep(
          state,
          [
            "responseCode",
            organizationId,
            projectId,
            environmentId,
            NEXT_DEPLOYMENT_ID
          ],
          action.payload.code
        );
      })
      // UPDATE DEPLOYMENT SETTINGS
      .addCase(updateDeploymentSettings.pending, (state, action) => {
        const { organizationId, projectId, environmentId } = action.meta.arg;

        state.loadingSettings = true;

        setDeep(
          state,
          ["errors", organizationId, projectId, environmentId, "settings"],
          undefined
        );
      })
      .addCase(updateDeploymentSettings.fulfilled, (state, action) => {
        const { organizationId, projectId, environmentId } = action.meta.arg;

        state.loadingSettings = false;

        setDeep(
          state,
          ["settings", organizationId, projectId, environmentId],
          action.payload
        );
      })
      .addCase(updateDeploymentSettings.rejected, (state, action) => {
        const { organizationId, projectId, environmentId } = action.meta.arg;

        state.loadingSettings = false;

        setDeep(
          state,
          ["errors", organizationId, projectId, environmentId, "settings"],
          action.payload
        );
      });
  }
});

export default deploymentSlice.reducer;
